import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, Leaf, Fire, SlidersHorizontal } from '@phosphor-icons/react';
import { isCustomisable, displayPrice } from '@/services/catalog';
import { formatINR } from '@/lib/money';

// Reusable product card used across Homepage, Collections, Categories, Search and POS.
// Presentational: parent supplies cart quantity + handlers so the same card serves
// the shared cart engine everywhere.
export const ProductCard = ({ product, qty = 0, onAdd, onCustomize, onInc, onDec, layout = 'row' }) => {
  const customisable = isCustomisable(product);
  const price = displayPrice(product);
  const soldOut = product.available === false;

  const VegDot = () => (
    <span
      className={`inline-flex items-center justify-center w-3.5 h-3.5 border rounded-sm flex-shrink-0 ${product.is_veg ? 'border-green-600' : 'border-red-600'}`}
      title={product.is_veg ? 'Veg' : 'Non-veg'}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${product.is_veg ? 'bg-green-600' : 'bg-red-600'}`} />
    </span>
  );

  const Tags = () => (
    <div className="flex flex-wrap gap-1 mt-0.5">
      {(product.tags || []).slice(0, 2).map((t) => (
        <span key={t} className="text-[9px] font-bold uppercase tracking-wide bg-sspice/10 text-sspice px-1.5 py-0.5 rounded">
          {t}
        </span>
      ))}
    </div>
  );

  const Action = () => {
    if (soldOut) {
      return <span className="px-3 py-1.5 text-[11px] font-semibold text-gray-400 border border-gray-200 rounded-full">Sold out</span>;
    }
    if (customisable) {
      return (
        <button
          data-testid={`customize-btn-${product.id}`}
          onClick={() => onCustomize(product)}
          className="relative px-4 py-1.5 bg-white border border-sspice text-sspice text-xs font-bold rounded-full hover:bg-sspice hover:text-white transition-colors flex items-center gap-1"
        >
          {qty > 0 && (
            <span className="absolute -top-2 -right-1 bg-sspice text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{qty}</span>
          )}
          <SlidersHorizontal size={12} weight="bold" /> ADD
        </button>
      );
    }
    if (qty === 0) {
      return (
        <motion.button
          data-testid={`add-btn-${product.id}`}
          whileTap={{ scale: 0.95 }}
          onClick={() => onAdd(product)}
          className="px-5 py-1.5 bg-sspice text-white text-xs font-bold rounded-full hover:bg-sspice-hover transition-colors"
        >
          ADD
        </motion.button>
      );
    }
    return (
      <div className="flex items-center gap-2 bg-sspice rounded-full px-1 py-0.5">
        <button data-testid={`minus-btn-${product.id}`} onClick={() => onDec(product)} className="w-6 h-6 flex items-center justify-center text-white">
          <Minus size={14} weight="bold" />
        </button>
        <span className="text-white text-xs font-bold min-w-[16px] text-center">{qty}</span>
        <button data-testid={`plus-btn-${product.id}`} onClick={() => onInc(product)} className="w-6 h-6 flex items-center justify-center text-white">
          <Plus size={14} weight="bold" />
        </button>
      </div>
    );
  };

  const Image = ({ className }) => (
    <div className={`rounded-xl bg-gray-50 overflow-hidden flex-shrink-0 ${className}`}>
      {product.image_url ? (
        <img
          src={product.image_url}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className={`w-full h-full object-cover ${soldOut ? 'grayscale opacity-60' : ''}`}
          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
        />
      ) : null}
      <div className={`w-full h-full items-center justify-center ${product.image_url ? 'hidden' : 'flex'}`}>
        <Fire size={24} weight="duotone" className="text-sspice/40" />
      </div>
    </div>
  );

  if (layout === 'grid') {
    return (
      <div data-testid={`product-card-${product.id}`} className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all">
        <Image className="h-32 w-full rounded-none" />
        <div className="p-3">
          <div className="flex items-center gap-1.5">
            <VegDot />
            <h3 className="font-semibold text-sm text-gray-900 truncate">{product.name}</h3>
          </div>
          <Tags />
          <div className="flex items-center justify-between mt-2">
            <span className="text-sm font-bold text-gray-900">{price.from ? 'from ' : ''}{formatINR(price.amount)}</span>
            <Action />
          </div>
        </div>
      </div>
    );
  }

  // row layout
  return (
    <div data-testid={`product-card-${product.id}`} className="flex gap-3 p-3 bg-white border border-gray-100 rounded-2xl shadow-sm">
      <Image className="w-20 h-20" />
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <VegDot />
            <h3 className="font-semibold text-sm text-gray-900 truncate">{product.name}</h3>
          </div>
          {product.description && <p className="text-xs text-gray-400 line-clamp-2">{product.description}</p>}
          <Tags />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm font-bold text-gray-900">{price.from ? 'from ' : ''}{formatINR(price.amount)}</span>
          <Action />
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
